import type { Group, Expense, Tab, UserProfile } from '../types';

export interface BalanceBreakdown {
    source: 'group' | 'tab';
    sourceId: string;
    label: string;       // group or tab name
    amount: number;       // positive = they owe me, negative = I owe them
    currency: string;
}

export interface FriendBalance {
    friendId: string;
    displayName: string;
    photoURL?: string;
    isManual?: boolean;
    /** Keyed by currency, e.g. { "GBP": 12.50, "USD": -3.00 } */
    netByCurrency: Record<string, number>;
    breakdown: BalanceBreakdown[];
}

/**
 * Compute outstanding balances from Groups (expenses) and Tabs.
 *
 * For Groups:
 *   - If I am the payer → each person in splitWith (excl. me) owes me their amount
 *   - If someone else paid → I owe them my splitWith amount
 *
 * For Tabs:
 *   - The tab owner paid.  Each member's share = sum of their claimed item portions,
 *     scaled by (1 + tipPct/100 + serviceChargePct/100).
 *   - If I'm the owner → members owe me their share.
 *   - If I'm a member → I owe the owner my share.
 */
export function computeBalances(
    userId: string,
    groups: Group[],
    expensesByGroup: Record<string, Expense[]>,
    tabs: Tab[],
    profiles: Map<string, UserProfile>,
): FriendBalance[] {
    // friendId → running FriendBalance
    const map = new Map<string, FriendBalance>();

    const getOrCreate = (friendId: string): FriendBalance => {
        let fb = map.get(friendId);
        if (!fb) {
            const profile = profiles.get(friendId);
            fb = {
                friendId,
                displayName: profile?.displayName ?? 'Unknown',
                ...(profile?.photoURL && { photoURL: profile.photoURL }),
                netByCurrency: {},
                breakdown: [],
            };
            map.set(friendId, fb);
        }
        return fb;
    };

    const addAmount = (fb: FriendBalance, currency: string, amount: number) => {
        fb.netByCurrency[currency] = (fb.netByCurrency[currency] ?? 0) + amount;
    };

    // ── Group Expenses ──────────────────────────────────────────────
    for (const group of groups) {
        const expenses = expensesByGroup[group.id] ?? [];
        for (const expense of expenses) {
            if (expense.payerId === userId) {
                // I paid → everyone in splitWith (except me) owes me
                for (const split of expense.splitWith) {
                    if (split.userId === userId) continue;
                    const fb = getOrCreate(split.userId);
                    addAmount(fb, expense.currency, split.amount);
                    fb.breakdown.push({
                        source: 'group',
                        sourceId: group.id,
                        label: group.name,
                        amount: split.amount,
                        currency: expense.currency,
                    });
                }
            } else {
                // Someone else paid → I owe the payer my split
                const mySplit = expense.splitWith.find(s => s.userId === userId);
                if (mySplit && mySplit.amount > 0) {
                    const fb = getOrCreate(expense.payerId);
                    addAmount(fb, expense.currency, -mySplit.amount); // negative = I owe them
                    fb.breakdown.push({
                        source: 'group',
                        sourceId: group.id,
                        label: group.name,
                        amount: -mySplit.amount,
                        currency: expense.currency,
                    });
                }
            }
        }
    }

    // ── Tabs ────────────────────────────────────────────────────────
    for (const tab of tabs) {
        const multiplier = 1 + (tab.tipPercentage || 0) / 100 + (tab.serviceChargePercentage || 0) / 100;

        // Compute each member's raw share (before tip/service)
        const shareByMember = new Map<string, number>();
        for (const item of tab.items) {
            if (item.claimedBy.length === 0) continue;
            const perPerson = item.price / item.claimedBy.length;
            for (const uid of item.claimedBy) {
                shareByMember.set(uid, (shareByMember.get(uid) ?? 0) + perPerson);
            }
        }

        // Apply tip/service multiplier
        for (const [uid, raw] of shareByMember) {
            shareByMember.set(uid, raw * multiplier);
        }

        if (tab.ownerId === userId) {
            // I'm the owner → everyone else owes me their share
            for (const [memberId, share] of shareByMember) {
                if (memberId === userId) continue;
                // Skip manual members (they start with a non-uid prefix typically)
                // Manual members have IDs like "manual_xxx", skip them for friends
                if (tab.manualMembers?.some(m => m.id === memberId)) continue;

                const fb = getOrCreate(memberId);
                addAmount(fb, tab.currency, share);
                fb.breakdown.push({
                    source: 'tab',
                    sourceId: tab.id,
                    label: tab.name,
                    amount: share,
                    currency: tab.currency,
                });
            }
        } else {
            // Someone else owns the tab → I owe the owner my share
            const myShare = shareByMember.get(userId) ?? 0;
            if (myShare > 0) {
                const fb = getOrCreate(tab.ownerId);
                addAmount(fb, tab.currency, -myShare);
                fb.breakdown.push({
                    source: 'tab',
                    sourceId: tab.id,
                    label: tab.name,
                    amount: -myShare,
                    currency: tab.currency,
                });
            }
        }
    }

    // Filter out zero-balance friends (all currencies zero)
    const result: FriendBalance[] = [];
    for (const fb of map.values()) {
        const hasNonZero = Object.values(fb.netByCurrency).some(v => Math.abs(v) > 0.005);
        if (hasNonZero) {
            // Round amounts
            for (const cur of Object.keys(fb.netByCurrency)) {
                const val = fb.netByCurrency[cur];
                if (val !== undefined) {
                    fb.netByCurrency[cur] = Math.round(val * 100) / 100;
                }
            }
            result.push(fb);
        }
    }

    // Sort: largest absolute balance first
    result.sort((a, b) => {
        const aMax = Math.max(...Object.values(a.netByCurrency).map(Math.abs));
        const bMax = Math.max(...Object.values(b.netByCurrency).map(Math.abs));
        return bMax - aMax;
    });

    return result;
}
